import { SectionTitle } from "@/sprout/typography";
import { useRef, useState } from "react";
import { router } from "@inertiajs/react";
import { useQueryClient } from "@tanstack/react-query";
import Tooltip from "@sprout/tooltip.tsx";
import QuestionIcon from "@sprout/icons/question.tsx";
import LoadingSpinner from "@sprout/loading_spinner.tsx";
import Pagination from "@components/pagination";
import JobRecommendationCard, { type JobRecommendation } from "./job_recommendation_card.tsx";
import { useShowFlash } from "@sprout/flash.tsx";
import useTranslation from "@/hooks/use_translation";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ApplicationForm } from "@components/job_post/application_form";
import { ApplicationSuccess } from "@components/job_post/application_success";
import DiscardProgressModal from "@components/job_post/discard_progress_modal";
import type { JobPost, SearchSpecificFields } from "@/types/jben/job_post";
import type { BoardConfiguration } from "@/types/jben/board_configuration";
import { useSafePostHog } from "@hooks/use_posthog";
import useRecommendations, {
  recommendationsQueryKey,
  usePrepareConfirmationRecommendations,
} from "@/queries/use_recommendations.ts";
import useRecommendationFeedback from "@/queries/use_recommendation_feedback.ts";
import { cn } from "@/lib/utils";

type ApplyModalData = {
  jobPost: JobPost & SearchSpecificFields;
  boardConfiguration: BoardConfiguration;
  submitPath: string;
  educationApiUrl?: string;
  dreamJobAvailable?: boolean;
};

const JobRecommendationsLoading = () => {
  const { t } = useTranslation("dashboard");

  return (
    <div className="min-w-0 rounded-lg bg-white p-6 flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <SectionTitle>{t("job_recommendations.title")}</SectionTitle>
        <Tooltip text={t("job_recommendations.tooltip")}>
          <QuestionIcon size="sm" />
        </Tooltip>
      </div>
      <div className="flex justify-center py-8">
        <LoadingSpinner size="medium" />
      </div>
    </div>
  );
};

const JobRecommendationsContent = ({
  jobRecommendations,
}: {
  jobRecommendations: JobRecommendation[];
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [applyModalData, setApplyModalData] = useState<ApplyModalData | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const dirtyRef = useRef<() => boolean>(() => false);

  const showFlash = useShowFlash();
  const { t } = useTranslation("dashboard");
  const posthog = useSafePostHog();
  const queryClient = useQueryClient();
  const submitFeedback = useRecommendationFeedback("dashboard");
  const prepareConfirmationRecommendations = usePrepareConfirmationRecommendations();

  const selected =
    jobRecommendations[selectedIndex > jobRecommendations.length - 1 ? 0 : selectedIndex];

  const handleViewJob = async (job: JobRecommendation) => {
    posthog?.capture("card_clicked", {
      card_type: "recommendation",
      source: "dashboard",
      job_post_id: job.id,
      job_title: job.title,
      company_name: job.companyName,
      previously_viewed: job.viewed,
    });
    if (!job.viewed) {
      queryClient.setQueryData<JobRecommendation[]>(recommendationsQueryKey(), (current) =>
        current?.map((rec) => (rec.id === job.id ? { ...rec, viewed: true } : rec)),
      );
    }
    try {
      const response = await fetch(job.viewJobPath, {
        headers: { "X-Modal-Request": "true", Accept: "application/json" },
      });
      if (!response.ok) throw new Error(`Request failed with status ${response.status}`);
      const data = await response.json();
      setApplyModalData(data);
      setSubmitted(false);
    } catch {
      showFlash(t("errors.there_was_a_problem"), "error");
    }
  };

  const handleApplySuccess = async () => {
    if (applyModalData) {
      await prepareConfirmationRecommendations(applyModalData.jobPost.id);
    }

    setSubmitted(true);
  };

  const closeModal = () => {
    setApplyModalData(null);
    setSubmitted(false);
    setShowDiscardConfirm(false);
  };

  return (
    <div className="min-w-0 rounded-lg bg-white p-6 flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <SectionTitle>{t("job_recommendations.title")}</SectionTitle>
        <Tooltip text={t("job_recommendations.tooltip")}>
          <QuestionIcon size="sm" />
        </Tooltip>
      </div>

      <JobRecommendationCard
        job={selected}
        onViewJob={(e) => {
          e.preventDefault();
          handleViewJob(selected);
        }}
        feedback={selected.feedback}
        onFeedback={(vote) => submitFeedback(selected, vote)}
      />

      {jobRecommendations.length > 1 && (
        <Pagination
          currentPage={selectedIndex + 1}
          totalPages={jobRecommendations.length}
          onPageChange={(page) => setSelectedIndex(page - 1)}
          center
        />
      )}

      {applyModalData && <div className="fixed inset-0 z-40 bg-gray-900/30" aria-hidden="true" />}
      <Dialog
        open={!!applyModalData}
        modal={false}
        onOpenChange={(open) => {
          if (open) return;
          if (dirtyRef.current() && !submitted) {
            setShowDiscardConfirm(true);
          } else {
            closeModal();
          }
        }}
      >
        <DialogContent
          className={cn(
            "w-[90vw] max-h-[90vh] flex flex-col overflow-hidden p-0 gap-0",
            submitted ? "sm:max-w-[600px]" : "sm:max-w-[900px]",
          )}
          hideClose={false}
          closeButtonClassName="z-10 rounded-full bg-white! opacity-100 shadow-md [&>svg]:size-8"
        >
          <div className="min-h-0 overflow-y-auto p-8">
            {applyModalData &&
              (submitted ? (
                <ApplicationSuccess
                  jobPost={applyModalData.jobPost}
                  jobRecommendationsEnabled
                  onViewJobs={() => router.visit("/jobs")}
                />
              ) : (
                <ApplicationForm
                  jobPost={applyModalData.jobPost}
                  questions={applyModalData.jobPost.questions}
                  submitPath={applyModalData.submitPath}
                  boardConfiguration={applyModalData.boardConfiguration}
                  onSuccess={handleApplySuccess}
                  educationApiUrl={applyModalData.educationApiUrl}
                  dreamJobAvailable={applyModalData.dreamJobAvailable}
                  isRecommendation
                  dirtyRef={dirtyRef}
                />
              ))}
          </div>
        </DialogContent>
      </Dialog>

      <DiscardProgressModal
        open={showDiscardConfirm}
        onOpenChange={setShowDiscardConfirm}
        onDiscard={closeModal}
      />
    </div>
  );
};

const JobRecommendationsSection = () => {
  const { data: jobRecommendations, isPending } = useRecommendations();

  if (isPending) return <JobRecommendationsLoading />;
  if (!jobRecommendations?.length) return null;

  return <JobRecommendationsContent jobRecommendations={jobRecommendations} />;
};

export default JobRecommendationsSection;
